import { useEffect, useState } from 'react'
import { notifications } from '@mantine/notifications'
import { useOSAtividades, useOSAtividadeUpdate } from '../../hooks/useOSAtividades'
import { extractApiError } from '../../lib/extractApiError'
import type { OrdemServicoAtividade, OrdemServicoAtividadeUpdate } from '../../types'

interface AtividadesOSProps {
  ordemServicoId: string
  /** OS em estado terminal (aprovada/cancelada): backend recusa PATCH com 400. */
  readonly?: boolean
}

/** data_execucao vem como 'YYYY-MM-DD'; formata sem passar por Date pra não
 *  cair pro dia anterior por causa do fuso (UTC-3). */
function formatarData(d: string | null): string {
  if (!d) return '—'
  const [ano, mes, dia] = d.split('-')
  if (ano && mes && dia) return `${dia}/${mes}/${ano}`
  return d
}

const TH_STYLE: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  borderBottom: '2px solid #E0E0E0',
  fontSize: '0.8rem',
  color: '#555',
  whiteSpace: 'nowrap',
}

const TD_STYLE: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid #F0F0F0',
  fontSize: '0.9rem',
  verticalAlign: 'top',
}

const INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  padding: '0.35rem 0.5rem',
  fontSize: '0.85rem',
  border: '1px solid #D0D0D0',
  borderRadius: '4px',
}

interface LinhaProps {
  atividade: OrdemServicoAtividade
  readonly: boolean
  onPatch: (data: OrdemServicoAtividadeUpdate) => Promise<void>
  salvando: boolean
}

/** Uma linha editável da tabela. Mantém estado local dos campos de texto e só
 *  faz PATCH no blur quando o valor muda (evita request por keystroke). */
function LinhaAtividade({ atividade, readonly, onPatch, salvando }: LinhaProps) {
  const [medicao, setMedicao] = useState(atividade.medicao_valor)
  const [observacao, setObservacao] = useState(atividade.observacao)
  const [resultado, setResultado] = useState(
    atividade.resultado ?? (atividade.executada ? 'ok' : 'pendente'),
  )

  // Reseta o estado local quando o servidor devolve novos valores (ex.: outra
  // edição invalidou a query e trouxe dados frescos).
  useEffect(() => {
    setMedicao(atividade.medicao_valor)
  }, [atividade.medicao_valor])
  useEffect(() => {
    setObservacao(atividade.observacao)
  }, [atividade.observacao])
  useEffect(() => {
    setResultado(atividade.resultado ?? (atividade.executada ? 'ok' : 'pendente'))
  }, [atividade.resultado, atividade.executada])

  const desabilitado = readonly || salvando

  function commitMedicao() {
    if (medicao === atividade.medicao_valor) return
    void onPatch({ medicao_valor: medicao })
  }

  function commitObservacao() {
    if (observacao === atividade.observacao) return
    if ((resultado === 'nao_ok' || resultado === 'nao_executada') && !observacao.trim()) return
    void onPatch({ observacao, resultado })
  }

  function alterarResultado(novo: typeof resultado) {
    setResultado(novo)
    if (novo !== 'nao_ok' && novo !== 'nao_executada') {
      void onPatch({ resultado: novo })
    } else if (observacao.trim()) {
      void onPatch({ resultado: novo, observacao })
    }
  }

  return (
    <tr data-testid={`atividade-${atividade.id}`}>
      <td style={TD_STYLE}>
        {readonly ? (
          <strong>
            {({
              pendente: 'Pendente',
              ok: 'OK',
              nao_ok: 'Não OK',
              nao_executada: 'Não executada',
              nao_aplicavel: 'Não aplicável',
            } as const)[resultado]}
          </strong>
        ) : (
          <select
            value={resultado}
            disabled={desabilitado}
            aria-label="Resultado da atividade"
            data-testid={`atividade-resultado-${atividade.id}`}
            onChange={(e) => alterarResultado(e.target.value as typeof resultado)}
            style={INPUT_STYLE}
          >
            <option value="pendente">Pendente / não verificada</option>
            <option value="ok">OK / executada</option>
            <option value="nao_ok">Não OK</option>
            <option value="nao_executada">Não executada</option>
            <option value="nao_aplicavel">Não aplicável</option>
          </select>
        )}
      </td>
      <td style={{ ...TD_STYLE, minWidth: '220px' }}>{atividade.descricao_atividade}</td>
      <td style={{ ...TD_STYLE, whiteSpace: 'nowrap' }}>{formatarData(atividade.data_execucao)}</td>
      <td style={{ ...TD_STYLE, minWidth: '120px' }}>
        {readonly ? (
          atividade.medicao_valor || '—'
        ) : (
          <input
            type="text"
            value={medicao}
            disabled={salvando}
            onChange={(e) => setMedicao(e.target.value)}
            onBlur={commitMedicao}
            placeholder="—"
            aria-label="Valor da medição"
            data-testid={`atividade-medicao-${atividade.id}`}
            style={INPUT_STYLE}
          />
        )}
      </td>
      <td style={{ ...TD_STYLE, minWidth: '180px' }}>
        {readonly ? (
          <span style={{ color: atividade.observacao ? 'inherit' : '#999' }}>
            {atividade.observacao || '—'}
          </span>
        ) : (
          <textarea
            value={observacao}
            disabled={salvando}
            rows={2}
            onChange={(e) => setObservacao(e.target.value)}
            onBlur={commitObservacao}
            placeholder="Observação (opcional)"
            aria-label="Observação"
            data-testid={`atividade-observacao-${atividade.id}`}
            style={{ ...INPUT_STYLE, resize: 'vertical' }}
          />
        )}
        {!readonly &&
          (resultado === 'nao_ok' || resultado === 'nao_executada') &&
          !observacao.trim() && (
            <small style={{ color: '#C62828', display: 'block', marginTop: 4 }}>
              Informe o motivo para salvar este resultado.
            </small>
          )}
      </td>
    </tr>
  )
}

export default function AtividadesOS({ ordemServicoId, readonly = false }: AtividadesOSProps) {
  const { data, isLoading, error } = useOSAtividades(ordemServicoId)
  const update = useOSAtividadeUpdate(ordemServicoId)

  const items = data?.results ?? []
  const total = data?.count ?? items.length
  const executadas = items.filter((a) => a.executada).length

  async function patchAtividade(atividadeId: string, payload: OrdemServicoAtividadeUpdate) {
    try {
      await update.mutateAsync({ atividadeId, data: payload })
      notifications.show({ color: 'green', message: 'Atividade atualizada.' })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível salvar',
        message: extractApiError(err, 'Erro ao atualizar a atividade.'),
      })
    }
  }

  return (
    <section
      data-testid="atividades-section"
      style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        marginBottom: '1.5rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 0 }}>Atividades / Checklist</h2>
        {total > 0 && (
          <span data-testid="atividades-progresso" style={{ fontSize: '0.85rem', color: '#666' }}>
            {executadas} de {total} executada{total === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {readonly && total > 0 && (
        <p
          data-testid="atividades-readonly-aviso"
          style={{ marginTop: '0.5rem', marginBottom: 0, fontSize: '0.8rem', color: '#888' }}
        >
          OS em estado terminal — as atividades estão em modo somente leitura.
        </p>
      )}

      {isLoading && <p style={{ color: '#666' }}>Carregando atividades...</p>}

      {!isLoading && error && (
        <p data-testid="atividades-erro" style={{ color: '#C62828' }}>
          Erro ao carregar as atividades desta OS.
        </p>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div
          data-testid="atividades-vazio"
          style={{
            marginTop: '1rem',
            padding: '1.5rem',
            background: '#FAFAFA',
            border: '1px dashed #D0D0D0',
            borderRadius: '6px',
            textAlign: 'center',
            color: '#666',
          }}
        >
          <p style={{ margin: 0, fontWeight: 600, color: '#444' }}>Esta OS não tem atividades.</p>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.85rem' }}>
            As atividades são geradas a partir do plano do PMOC. OS criadas
            manualmente podem não ter checklist associado.
          </p>
        </div>
      )}

      {!isLoading && !error && items.length > 0 && (
        <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
          <table
            data-testid="atividades-lista"
            style={{ width: '100%', borderCollapse: 'collapse' }}
          >
            <thead>
              <tr>
                <th style={TH_STYLE}>Status</th>
                <th style={TH_STYLE}>Atividade</th>
                <th style={TH_STYLE}>Execução</th>
                <th style={TH_STYLE}>Medição</th>
                <th style={TH_STYLE}>Observação</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <LinhaAtividade
                  key={a.id}
                  atividade={a}
                  readonly={readonly}
                  salvando={update.isPending}
                  onPatch={(payload) => patchAtividade(a.id, payload)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
